import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';

import UserContext from '../../context/users/userContext';
import CustomersContext from '../../context/otogas/customers/customersContext';

const AssignApproverModal = ({
  show,
  handleClose,
  submitting = false,          // external loader
  setSubmitting = () => {},    // external setter from parent
  onSaved,                     // callback after success
}) => {
  const { users, fetchUsers } = useContext(UserContext);
  const { addPriceApprover } = useContext(CustomersContext); // MUST accept (userCode)

  const [approverUserCode, setApproverUserCode] = useState('');
  const lastFetchRef = useRef(0); // debounce fetchUsers

  // Build options (value=userCode as STRING, label="First Last")
  const userOptions = useMemo(() => {
    const list = Array.isArray(users)
      ? users
      : Array.isArray(users?.data) ? users.data
      : Array.isArray(users?.responseObject) ? users.responseObject
      : Array.isArray(users?.responseObject?.data) ? users.responseObject.data
      : [];
    return list
      .map((u) => ({
        value: String(u.userCode ?? u.id ?? u.code ?? ''),
        label:
          [u.firstName, u.lastName].filter(Boolean).join(' ')
          || u.name || u.userName || u.email || 'Unknown',
      }))
      .filter((o) => o.value);
  }, [users]);

  const selectedOption = useMemo(
    () => userOptions.find((o) => o.value === approverUserCode) || null,
    [userOptions, approverUserCode]
  );

  useEffect(() => {
    if (!show) return;
    const now = Date.now();
    if (now - lastFetchRef.current >= 3000) {
      lastFetchRef.current = now;
      fetchUsers && fetchUsers();
    }
    // Do NOT auto-clear user selection when modal reopens unless you want to:
    // setApproverUserCode('');
  }, [show, fetchUsers]);

  const save = async () => {
    if (!approverUserCode) return;
    setSubmitting(true);
    try {
      await addPriceApprover?.(approverUserCode);  // ← GLOBAL: only userCode
      await onSaved?.();
      handleClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Set Global Approver</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Select Approver</Form.Label>
            <Select
              options={userOptions}
              value={selectedOption}
              onChange={(opt) => setApproverUserCode(opt ? String(opt.value) : '')}
              placeholder="Choose a user..."
              isSearchable
              isClearable
            />
            {!approverUserCode && (
              <div className="invalid-feedback d-block">Approver is required</div>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={save} disabled={submitting || !approverUserCode}>
          {submitting ? (
            <>
              <Spinner as="span" size="sm" animation="border" className="me-2" />
              Saving...
            </>
          ) : (
            'Save Approver'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignApproverModal;
